import { vetor } from '../../dominio/geometria/vetor3'
import type { FaixaLongitudinal, PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { Barra } from '../componentes/Barra'
import { MaterialPeca, Peca } from '../componentes/Peca'
import { TONS_CARROCERIA } from '../../dominio/didatico/tokens'
import type { EstadoTerceiraPortaOficina, IdLadoCorteTerceiraPorta } from '../../dominio/didatico/oficinaTerceiraPorta'
import { CaixaArredondada } from '../componentes/CaixaArredondada'

const ABERTURA_RAD = 0.95

interface Props {
  perfil: PerfilVeiculo
  id: string
  faixa: FaixaLongitudinal
  lado: 'esquerdo' | 'direito'
  estadoOficina?: EstadoTerceiraPortaOficina
}

/**
 * Chapa maciça no lugar de uma porta traseira — o footprint de um veículo
 * tratado como carroceria de duas portas nesta oficina. Sem dobradiça, trava
 * ou vidro reais: a articulação em Y na borda dianteira é a mesma da porta
 * comum (`PortaDidatica`), reaproveitada aqui como a dobra que a técnica
 * preserva ao cortar os outros três lados.
 */
export function PainelTerceiraPortaDidatico({ perfil, id, faixa, lado, estadoOficina }: Props) {
  const sinal = lado === 'esquerdo' ? -1 : 1
  const x = sinal * (perfil.largura / 2 - 0.015)
  const comprimento = faixa.zDianteiro - faixa.zTraseiro
  const yBaixo = perfil.topoSoleiraY
  const yCintura = perfil.cinturaY
  const alturaChapa = yCintura - yBaixo
  const centroZ = -comprimento / 2

  if (estadoOficina?.removida) return null

  const ladoCortado = (ladoCorte: IdLadoCorteTerceiraPorta) => estadoOficina?.lados[ladoCorte] === 'cortado'
  const aberturaOficina = estadoOficina
    ? (sinal < 0 ? ABERTURA_RAD : -ABERTURA_RAD) * estadoOficina.abertura
    : 0

  return (
    <Peca
      id={id}
      position={[x, 0, faixa.zDianteiro]}
      rotation={[0, aberturaOficina, 0]}
      deslocamentoAfastado={[sinal * 0.5, 0, 0]}
    >
      <mesh position={[0, yBaixo + alturaChapa / 2, centroZ]}>
        <CaixaArredondada args={[0.045, alturaChapa, comprimento - 0.03]} />
        <MaterialPeca id={id} corBase={TONS_CARROCERIA.porta} acabamento="pintura" />
      </mesh>

      {/* linhas de corte: cada lado muda de cor quando o professor/aluno o corta */}
      <Barra
        id={`${id}.corteSuperior`}
        de={vetor(sinal * -0.022, yCintura, 0.01)}
        para={vetor(sinal * -0.022, yCintura, -comprimento + 0.01)}
        largura={0.02}
        espessura={0.02}
        corBase={ladoCortado('superior') ? '#55d98a' : '#8a8f94'}
      />
      <Barra
        id={`${id}.corteInferior`}
        de={vetor(sinal * -0.022, yBaixo, 0.01)}
        para={vetor(sinal * -0.022, yBaixo, -comprimento + 0.01)}
        largura={0.02}
        espessura={0.02}
        corBase={ladoCortado('inferior') ? '#55d98a' : '#8a8f94'}
      />
      <Barra
        id={`${id}.corteTraseiro`}
        de={vetor(sinal * -0.022, yBaixo, -comprimento + 0.01)}
        para={vetor(sinal * -0.022, yCintura, -comprimento + 0.01)}
        largura={0.02}
        espessura={0.02}
        corBase={ladoCortado('traseiro') ? '#55d98a' : '#8a8f94'}
      />
    </Peca>
  )
}
